import { Maths } from 'helpers/maths';
import { convertNumberToDesiredFormat } from 'helpers/transformHelpers';
import resolveUnitPrice from 'screens/Invoices/redesign/helpers/resolveUnitPrice';
import { InvoiceFormProduct } from '../../../../Constants/invoiceFormikData';

// FR-104: preserve full user-entered precision for the interrelated Total
// Product / Price per Unit / Total Cost inputs — reuse the same Decimal-based
// arithmetic (`Maths`) the Applications Product Details drawer + Tank Info use
// (via FormInputWithPrecisionDisplay) instead of the previous 2-decimal
// truncation (`Math.round(v * 100) / 100`).
export const mul = (a: number, b: number): number =>
  Maths.multiply(a, b).toNumber();
export const div = (a: number, b: number): number =>
  b !== 0 ? Maths.divide(a, b).toNumber() : 0;

// Sign agreement is compared term by term rather than through the product of
// the two values, so no floating-point multiply sits in the middle of a
// comparison whose only interest is the sign. Zero on either side agrees with
// everything.
export const signsDiffer = (a: number, b: number): boolean =>
  (a > 0 && b < 0) || (a < 0 && b > 0);
export const signsAgree = (a: number, b: number): boolean => !signsDiffer(a, b);

// Credit/return lines carry negative quantities and costs, so a single leading
// minus is allowed alongside digits and one decimal point. Everything else
// (letters, e/+, extra dots or minuses) is stripped as the user types.
export const sanitizeNumericInput = (raw: string): string => {
  const negative = raw.trimStart().startsWith('-');
  const digits = raw.replace(/[^0-9.]/g, '').replace(/\.(?=.*\.)/g, '');
  return negative ? `-${digits}` : digits;
};

// Price per Unit is a rate, never a signed amount: on a credit/return line the
// minus belongs on the quantity and the total cost, which multiply back to a
// positive rate. So the minus is refused at the input rather than accepted and
// then stripped, which would leave the caret fighting a vanishing character.
export const sanitizeRateInput = (raw: string): string =>
  sanitizeNumericInput(raw).replace(/^-/, '');

// Display helper: always show at least two decimal places (so cost/price read
// as currency and the "0.00" default holds) while NEVER dropping any
// precision the user entered beyond two places.
export const formatMinPrecision = (value: number, min = 2): string => {
  if (!Number.isFinite(value)) return '';
  // String(value) switches to exponential notation outside roughly the
  // 1e-6..1e21 range (e.g. "5e-7", "1e+21"). Reposition the decimal point
  // among the exact digits JS already chose (its shortest round-trip
  // representation) rather than expanding via toFixed(), which has a fixed
  // digit cap and, past ~17 significant digits, starts exposing the value's
  // binary floating-point representation instead of the intended decimal.
  let stringValue = String(value);
  const exponential = /^(-?)(\d)(?:\.(\d+))?e([+-]\d+)$/i.exec(stringValue);
  if (exponential) {
    const [, sign, leadDigit, fracDigits = '', expStr] = exponential;
    const exponent = Number(expStr);
    const digits = leadDigit + fracDigits;
    if (exponent < 0) {
      stringValue = `${sign}0.${'0'.repeat(-exponent - 1)}${digits}`;
    } else if (exponent + 1 >= digits.length) {
      stringValue = `${sign}${digits}${'0'.repeat(
        exponent + 1 - digits.length
      )}`;
    } else {
      stringValue = `${sign}${digits.slice(0, exponent + 1)}.${digits.slice(
        exponent + 1
      )}`;
    }
  }
  const [intPart, decPart = ''] = stringValue.split('.');
  return decPart.length >= min
    ? `${intPart}.${decPart}`
    : `${intPart}.${decPart.padEnd(min, '0')}`;
};

// Display helper for Total Invoiced Quantity while it is NOT focused: always
// rounds to 2 decimals, identically to Price per Unit / Total Cost's blurred
// display — including a derived value so small it rounds to "0.00" while
// genuinely non-zero; the full-precision value is still one click away via
// focus. A non-finite value (e.g. an overflowed division, or a typed value
// with no practical digit limit) is kept distinct from a real zero rather
// than reported as "0.00".
/**
 * Two-decimal display with thousands separators, matching the products table and
 * the Applications drawer. Only ever the resting display — focusing swaps in the
 * raw number — so a separator never reaches `Number()` or the input sanitiser.
 *
 * The rounding is `toFixed`'s, not `Intl`'s: they disagree on halfway values
 * (5.555 → "5.55" vs "5.56"), and this is a grouping change, not a rounding one.
 */
export const groupDigits = (value: number): string =>
  convertNumberToDesiredFormat(Number(value.toFixed(2)), 'fixed', 2) as string;

export const formatQuantityDisplay = (value: number): string => {
  if (!Number.isFinite(value)) return formatMinPrecision(value);
  return groupDigits(value);
};

/**
 * The rate and unit a stored line should open with, resolved exactly as the
 * invoice tables resolve theirs: the pair the line carries, or — when either
 * half of it is missing — the rate the cost and quantity divide out to, in the
 * unit the line was invoiced in. Sharing `resolveUnitPrice` is what keeps the
 * drawer and the tables from showing the same line two different ways.
 */
export const deriveOpeningRate = (
  product?: InvoiceFormProduct
): { value: number; unit: string } => {
  if (!product) return { value: 0, unit: '' };
  return (
    resolveUnitPrice({
      storedValue: product.priceValue,
      storedUnit: product.priceUnit,
      quantityValue: product.totalQuantityValue,
      quantityUnit: product.totalQuantityUnit,
      costValue: product.totalCostValue
    }) ?? { value: 0, unit: '' }
  );
};
